import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type MasterDataListResponseData } from '@ev-tech/search-modules-api';

import { getShopsQueryKey } from './queryKeys';
import { useAppContext } from '../useAppContext';
import { useSearchStore } from '../stores/search/useSearchStore';

export const useShopsByIds = () => {
  const { api } = useAppContext();
  const masterDataShopIds = useSearchStore((state) => state.masterDataShopIds);
  const shopIdsFromStore = useSearchStore((state) => state.shopIds);
  const queryClient = useQueryClient();

  const shopIds = useMemo(() => {
    const ids = masterDataShopIds ?? shopIdsFromStore ?? [];
    return [...new Set(ids.filter(Boolean))];
  }, [masterDataShopIds, shopIdsFromStore]);

  const {
    data: shops,
    isFetched,
    isError,
    ...query
  } = useQuery({
    queryKey: getShopsQueryKey({ shopIds }),
    queryFn: async () => {
      const result = await api.getShopsByIds(shopIds);
      if (result.status === 'error') throw result.error;
      return result.data;
    },
    enabled: shopIds.length > 0,
    placeholderData: (previousData) => {
      if (previousData) return previousData;

      return queryClient.getQueryData<MasterDataListResponseData>(getShopsQueryKey({ shopIds }));
    },
  });

  const shopNames = Object.values(shops ?? {})
    .map((shop) => shop.name)
    .filter((name): name is string => name !== undefined);

  return {
    ...query,
    shops,
    shopNames,
    shopIds,
    isFetched,
    isError,
  };
};
